import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { HrefType } from '../../../../hdi-components/src/shared/enums/href-type.enums';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of, Subscription, switchMap, take } from 'rxjs';
import {
  CaasLayout,
  CaasStage,
  CaasInpageNav,
  CaasMainSection,
  CaasComponent,
  CaasInternalLink,
  Caasnewsticker,
  CaasDisruptor,
} from '../../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { MenuItem } from '../../../../hdi-components/src/shared/interfaces/caas/nav.interface';
import { CaasContentService } from '../../services/caas-content.service';
import { ImageService } from '../../services/image.service';
import {
  PodcastItems,
  PodcastPlatforms,
  TextBlockAlign,
  TextBlockTitleVariant,
} from 'hdi-components/source';
import {
  ImageMetaData,
  MetaData,
  ModalType,
  News,
  NewsFilter,
} from 'hdi-components';
import { ModalVideoService } from '../../services/modal-video.service';
import { SafeResourceUrl } from '@angular/platform-browser';

import { DOCUMENT } from '@angular/common';
import { NavigationService } from '../../services/navigation.service';

declare global {
  interface Window {
    TPP_SNAP: any;
  }
}

interface PageData {
  content: { layouts: CaasLayout[]; metaData: MetaData; identifier?: string };
  podcastData: { podcasts: PodcastItems[]; platforms: PodcastPlatforms[] };
  news: News[];
  newsFilters: NewsFilter[];
  events: CaasLayout[];
  imagesMetaData: ImageMetaData[];
  tags: { identifier: string; name: string }[];
}

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
})
export class HomePageComponent implements OnDestroy, OnInit {
  HrefType = HrefType;
  TextBlockTitleVariant = TextBlockTitleVariant;
  TextBlockAlign = TextBlockAlign;
  images: string[] = [];
  layouts?: CaasLayout[];
  pageCaasSubscription$: Subscription = new Subscription();
  stage?: CaasStage;
  inpageNav?: CaasInpageNav;
  mainSections?: CaasMainSection[];
  videoId?: string;
  videoSrc?: SafeResourceUrl;
  showModal = false;
  podcastData: { podcasts: PodcastItems[]; platforms: PodcastPlatforms[] } = {
    podcasts: [],
    platforms: [],
  };
  newsComponent: CaasComponent | undefined;
  news: News[] = [];
  newsFilters: { filter: string; categories: string[]; identifier: string }[] =
    [];
  filteredNews: News[] = [];
  imagesMetaData: ImageMetaData[] = [];
  tags: { name: string; identifier: string }[] = [];
  alternateBackground: ('white' | 'grey')[] = [];
  inpageMenu: MenuItem[] = [];
  events: CaasLayout[] = [];
  ModalType = ModalType;
  newsticker?: Caasnewsticker;
  breadcrumbs: { title: string; url?: string }[] = [];
  alert?: CaasDisruptor;
  public paddingTop = false;
  public paddingBlock = false;
  public paddingBottom = false;

  private readonly document = inject(DOCUMENT);
  private router = inject(Router);

  constructor(
    private readonly caasContentService: CaasContentService,
    private readonly routes: ActivatedRoute,
    private readonly imageServices: ImageService,
    private readonly modalVideoService: ModalVideoService,
    private readonly navigationServices: NavigationService
  ) {
    this.pageCaasSubscription$ = this.routes.data
      .pipe(
        switchMap((data) =>
          this.loadContentWithExtras(data?.['pageIdentifier'])
        )
      )
      .subscribe((data) => this.initializeComponent(data));
  }

  private loadContentWithExtras(pageIdentifier?: string): Observable<PageData> {
    if (!pageIdentifier) {
      return of(this.getEmptyContent());
    }

    return this.caasContentService.getContent(pageIdentifier).pipe(
      switchMap((content) => {
        if (this.document.defaultView!.TPP_SNAP) {
          this.document.defaultView!.TPP_SNAP.setPreviewElement(pageIdentifier);
        }
        const hasNews = this.hasNewsComponent(content);
        const news$ = hasNews
          ? this.caasContentService.getNews().pipe(take(1))
          : of([]);

        return forkJoin({
          content: of(content),
          podcastData: of({ podcasts: [], platforms: [] }),
          news: news$,
          newsFilters: of([]),
          events: this.caasContentService.getEvents(),
          imagesMetaData: this.caasContentService.getImagesMetaData(),
          tags: of([]),
        });
      })
    );
  }

  private initializeComponent({
    content,
    podcastData,
    news,
    newsFilters,
    events,
    tags,
  }: PageData): void {
    this.addTPPIntegration(content.identifier);
    this.layouts = content.layouts;
    this.initializeStage(content);
    this.initializeInpageNav(content);
    this.initializeDisruptor(content);
    this.initializeMainSections(content);

    this.podcastData = podcastData;
    this.news = news;
    this.tags = tags;
    this.newsFilters = newsFilters;
    this.events = events;

    this.getAlternateBackground();
    this.inpageMenu = this.getInpageNav();

    if (news) {
      this.imageServices.replaceNewsImages(
        this.filteredNews.length > 0 ? this.filteredNews : news
      );
    }

    if (podcastData) {
      this.imageServices.replacePodcastsImages(podcastData.podcasts);
    }
  }

  private hasNewsComponent(content: {
    layouts: CaasLayout[];
    metaData: MetaData;
  }): boolean {
    const mainLayout = content.layouts.find((layout) => layout.name === 'main');
    return (
      mainLayout?.children?.some((section: CaasMainSection) =>
        section?.formData?.st_items?.value?.some(
          (item: CaasComponent) => item?.template?.uid === 'st_news'
        )
      ) ?? false
    );
  }

  private initializeStage(content: {
    layouts: CaasLayout[];
    metaData: MetaData;
  }): void {
    const stageLayout = content.layouts.find(
      (layout) => layout.name === 'stage'
    );
    if (stageLayout?.children?.length) {
      this.stage = stageLayout.children.find(
        (child) => child.template?.uid === 'st_stage_home'
      ) as CaasStage;
      this.imageServices.replaceStageImages(this.stage);
    }
  }

  private initializeInpageNav(content: {
    layouts: CaasLayout[];
    metaData: MetaData;
  }): void {
    const inpageNavLayout = content.layouts.find(
      (layout) => layout.name === 'inpagenav'
    );
    if (inpageNavLayout?.children) {
      this.inpageNav = inpageNavLayout.children[0] as CaasInpageNav;
    }
  }

  private initializeDisruptor(content: {
    layouts: CaasLayout[];
    metaData: MetaData;
  }): void {
    const disruptor = content.layouts.find(
      (layout) => layout.name === 'disruptor' && layout.children?.length
    );
    if (disruptor?.children) {
      this.newsticker = disruptor.children.find(
        (child) => child.template.uid === 'st_ticker'
      ) as Caasnewsticker;

      this.alert = disruptor.children.find(
        (child) => child.template.uid === 'st_disruptor'
      ) as CaasDisruptor;
    }
  }

  private initializeMainSections(content: {
    layouts: CaasLayout[];
    metaData: MetaData;
  }): void {
    const mainSections = content.layouts.find(
      (layout) => layout.name === 'main'
    )?.children as CaasMainSection[];

    this.mainSections = mainSections?.filter((section) => {
      const item = section?.formData?.st_items?.value?.[0];
      const isNewsHub =
        item?.template?.uid === 'st_news' &&
        item?.formData?.st_layout?.value?.identifier === 'hub';

      if (isNewsHub) {
        this.newsComponent = item;
      }

      return !isNewsHub;
    });

    if (this.mainSections) {
      this.imageServices.replaceMainImages(this.mainSections);
    }
  }

  private getEmptyContent(): PageData {
    return {
      content: { layouts: [], metaData: {} },
      podcastData: { podcasts: [], platforms: [] },
      news: [],
      newsFilters: [],
      events: [],
      imagesMetaData: [],
      tags: [],
    };
  }

  ngOnInit() {
    this.openVideoModalByQueryParams();
  }

  updateNews(filteredNews: News[]): void {
    this.filteredNews = filteredNews;
  }

  getInternalUrl(internalLinkValue: CaasInternalLink['value']): string {
    if (internalLinkValue && internalLinkValue.uid === 'startseite') {
      return '/';
    } else {
      return '/404';
    }
  }

  getInpageNav(): MenuItem[] {
    return (this.mainSections ?? []).reduce((acc, section) => {
      if (
        section.formData.st_anchor_name?.value &&
        section.formData.st_inpagenav?.value
      ) {
        acc.push({
          title:
            section.formData.st_inpagenav.value.formData.lt_link_label.value ??
            '',
          url: section.formData.st_anchor_name.value,
          hoverText:
            section.formData.st_inpagenav.value.formData.lt_link_title?.value ??
            '',
        });
      }
      if (
        section.formData.st_items?.value &&
        section.formData.st_items.value.length > 0
      ) {
        section.formData.st_items.value.forEach((item) => {
          if (
            item.formData.st_anchor_name?.value &&
            item.formData.st_inpagenav?.value
          ) {
            acc.push({
              title:
                item.formData.st_inpagenav.value.formData.lt_link_label.value ??
                '',
              url: item.formData.st_anchor_name.value,
              hoverText:
                item.formData.st_inpagenav.value.formData.lt_link_title
                  ?.value ?? '',
            });
          }
        });
      }
      return acc;
    }, [] as MenuItem[]);
  }

  openVideoModalByQueryParams(): void {
    const { showModal, videoId, videoSrc } =
      this.modalVideoService.openModalVideo();
    this.showModal = showModal;
    this.videoId = videoId;
    this.videoSrc = videoSrc;
  }

  getWelcomeSectionContent(stage: CaasStage) {
    return stage.formData.st_header?.value;
  }

  getAlternateBackground() {
    let isWhite = (index: number) => index % 2 !== 0;
    this.mainSections?.forEach((section, index) => {
      if (section.formData.st_background_reset?.value) {
        this.alternateBackground.push('white');
        if (!isWhite(index) && !isWhite(0)) {
          isWhite = (index: number) => index % 2 === 0;
        } else {
          isWhite = (index: number) => index % 2 !== 0;
        }
      } else if (isWhite(index)) {
        this.alternateBackground.push('white');
      } else {
        this.alternateBackground.push('grey');
      }
    });
  }

  private addTPPIntegration(initialPreviewId: string | undefined): void {
    // TPP Integration Start
    if (this.document) {
      const main = this.document.querySelector('main');
      if (!main) {
        return;
      }
      main.setAttribute('data-preview-id', initialPreviewId || '');
      if (this.document.defaultView!.TPP_SNAP) {
        this.document.defaultView!.TPP_SNAP.setPreviewElement(initialPreviewId);
        this.document.defaultView!.TPP_SNAP.onRequestPreviewElement(
          async (previewId: string) => {
            let previewSeoRoute: string | null;
            this.navigationServices
              .getCloudNavigation()
              .pipe()
              .subscribe((nav) => {
                previewSeoRoute =
                  this.navigationServices.getSeoRouteByIdentifier(
                    previewId.split('.')[0],
                    nav
                  );
                if (previewSeoRoute) {
                  this.router.navigate([previewSeoRoute]);
                }
              });
          }
        );
      }
      // TPP Integration End
    }
  }

  ngOnDestroy(): void {
    this.pageCaasSubscription$.unsubscribe();
  }
}
